import { QueryObserverResult } from '@tanstack/react-query';
import { FormEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import useTradingAccount from 'hooks/useTradingAccount';

import useAuthModalStore from 'features/securities/AuthModal/store';
import ReferralForm from './ReferralForm';
import Confirm from './Modals/Confirm';
import Done from './Modals/Done';
import Template from './Template';

import { CommunityStatusDataPayload } from '../../../../@types/community';
import useJoinCommunity from '../hooks/useJoinCommunity';
import useGetCommunity from '../hooks/useGetCommunity';
import { DATA_CY_JOIN_COMMUNITY } from '../__tests__/constant';
import { affiliateActions, affiliateNavigationEvents } from '../analytics';

function JoinCommunity() {
  const showModal = useAuthModalStore((store) => store.showModal);
  const { isRealTrading } = useTradingAccount();
  const [confirm, setConfirm] = useState(false);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const router = useRouter();

  // A unique number to trigger `getCommunity.refetch`
  // whenever users click CTA because the same code got cached by react-query
  const [rand, setRand] = useState(0);

  const getCommunity = useGetCommunity(code);
  const { data: communityData } = getCommunity;
  const joinCommunity = useJoinCommunity();

  const triggerConfirm = ({
    isSuccess,
  }: QueryObserverResult<CommunityStatusDataPayload>) => {
    if (!isSuccess) {
      return;
    }

    if (isRealTrading) {
      setConfirm(true);
      affiliateNavigationEvents.confirmedCodeCommunityViewed(code);
      return;
    }
    showModal();
  };

  const onDone = () => {
    affiliateActions.activatedCodeSuccessSelected(code);

    setDone(false);
    const { room_id } = joinCommunity?.data?.community?.chat || {};
    const url = room_id ? `/group-chat/${room_id}` : '/stream';
    router.push(url);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const codeFromParams = formData.get('code') as string;

    affiliateActions.activatedCodeSelected(codeFromParams);

    setCode(codeFromParams);
    setRand(Math.random());
  };

  const onConfirm = () => {
    affiliateActions.activatedCodeConfirmed(code);

    joinCommunity.mutate(code, {
      onSuccess() {
        setConfirm(false);
        setDone(true);
        affiliateNavigationEvents.activatedSuccessCodeCommunityViewed(code);
      },
    });
  };

  const onCancel = () => {
    affiliateActions.activatedCodeCanceled(code);
    setConfirm(false);
  };

  useEffect(() => {
    if (code.length > 0) {
      getCommunity.refetch().then(triggerConfirm);
    }
  }, [code, rand, isRealTrading]);

  return (
    <div data-cy={DATA_CY_JOIN_COMMUNITY}>
      <Template>
        <ReferralForm error={getCommunity.isError} onSubmit={onSubmit} />
      </Template>
      <Confirm
        isRealTrading={isRealTrading}
        fee={communityData?.default_fee}
        onOk={onConfirm}
        title="Konfirmasi Kode Komunitas"
        until={communityData?.commit_until}
        visible={confirm && !getCommunity.isError}
        onCancel={onCancel}
        community={communityData?.community}
        communityFee={communityData?.community_fee}
        minBalance={Number(communityData?.community.minimum_balance)}
      />
      <Done
        onOk={onDone}
        name={communityData?.community?.name}
        leaderName={joinCommunity?.data?.community?.leader?.username}
        visible={done}
      />
    </div>
  );
}

export default JoinCommunity;
